import {memo, useCallback, useRef} from "react";
import {Logic, LogicProps} from "./SuggestionsPopup.interfaces";
import {useReducer} from "./SuggestionsPopup.reducer";
import {CheckboxProps, ComponentEventHandler, InputProps, PopperRefHandle, PopupProps} from "@fluentui/react-northstar";
import {CopilotApi} from "../../../../apis/copilotApi";
import {CopilotImageResult} from "../../../../Interfaces/CopilotImageResult";

const keepSameLogic = (prevProps: Readonly<LogicProps>, nextProps: Readonly<LogicProps>): boolean => {
    return (
        prevProps.message === nextProps.message
    )
}

export const SuggestionsPopupLogic = memo((props: LogicProps): JSX.Element => {
    const logic: Logic = useLogic(props);
    return props.children(logic);
}, keepSameLogic);

export const useLogic = (props: LogicProps) => {
    const {state, dispatch} = useReducer();
    const popperRef = useRef<PopperRefHandle | null>(null);

    const fetchSuggestions = useCallback(async () => {
        if (state.cancelToken) state.cancelToken.cancel();
        dispatch({type: "clearSuggestions"});
        const suggestions = await CopilotApi.getImagesSuggestions({
            message: state.prevMessage,
            keywords: state.keywords,
            creativeCommonOnly: state.creativeCommonOnly,
            language: props.teamsContext.fullLocale,
        }, cancelToken => {
            dispatch([{type: "setCancelToken", value: cancelToken}], false);
        });
        dispatch([
            {type: "setSuggestions", value: suggestions.images},
            {type: "setKeywords", value: suggestions.keywords},
        ]);
    }, []);

    const handleOpenChange = useCallback((message: string): ComponentEventHandler<PopupProps> => async (e, data) => {
        const isOpen = data?.open ?? false;
        const isSameMessage = state.prevMessage === message;
        if (!isOpen || isSameMessage) return;
        dispatch([
            {type: "setPrevMessage", value: message},
            {type: "setKeywords", value: ""},
        ]);
        await fetchSuggestions();
    }, []);

    const handleSelectSuggestion = useCallback((suggestion: CopilotImageResult) => () => {
        props.onSuggestionSelected({
            content: suggestion.url,
            imageLink: "@root/" + suggestion.url.slice(suggestion.url.lastIndexOf("/") + 1),
            fromSuggestions: true,
        });
    }, []);

    const handleToggleImageLicence: ComponentEventHandler<CheckboxProps> = useCallback(async () => {
        dispatch({type: "toggleCreativeCommonOnly"}, false);
        await fetchSuggestions();
    }, []);

    const handleChangeKeywords: ComponentEventHandler<InputProps> = useCallback(async (e, data) => {
        const value = (data?.value ?? "") + "";
        dispatch({type: "setKeywords", value});
        throttleInput(
            500,
            state.keywordsTimeout,
            timeout => dispatch({type: "setKeywordsTimeout", value: timeout}),
            fetchSuggestions
        )
    }, []);

    const isLoading = !state.suggestions;

    const disableTrigger = props.message.length < 10;

    return {
        ...props,
        ...state,
        isLoading,
        handleOpenChange,
        popperRef,
        handleSelectSuggestion,
        disableTrigger,
        handleToggleImageLicence,
        handleChangeKeywords
    }
}

///////////////////////////////////////////////////// PURE METHODS /////////////////////////////////////////////////////

const throttleInput = (
    throttling: number,
    timeout: NodeJS.Timeout | undefined,
    updateTimeout: (timeout: NodeJS.Timeout | undefined) => void,
    notifyChange: () => void) => {
    if (timeout) clearTimeout(timeout);
    const newTimeout = setTimeout(() => {
        notifyChange();
        updateTimeout(undefined);
    }, throttling);
    updateTimeout(newTimeout)
}